/**
 * Read-only summary table of spending phases, click a row to edit in
 * SpendingPhaseEditDrawer (#1538) — mirrors the Holdings table+drawer
 * pattern (#1533) reused here for consistency.
 */

import { PersonData, PersonDeceasedAge, SpendingPhase } from '../../types/wizard';
import { CATEGORY_ORDER, CATEGORY_META, formatPhaseAgeRange, formatPhaseCalendarYearRange, getCategoryItems, getCategoryTotal, getPhaseTotal, projectAmountToPhaseStart } from '../../utils/spendingPhaseHelpers';

export const MAX_SPENDING_PHASES = 5;
export const MIN_SPENDING_PHASES = 1;

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Italic "(≈£X at Yr N)" secondary line under the phase-total figure — same
 * visual pattern as the projection table's nominal/real-terms pairing
 * (`RealTermsValue`/`.real-terms` in ProjectionTable.tsx) — but deliberately
 * NOT the same CSS class, because this is
 * the inverse pairing: today's-money entered value + its inflated value at
 * the phase's start year. Shown only on the Total column (not per category)
 * to avoid repeating the same growth rate up to 4 times in one row — see
 * #1540 review. Hidden for phases starting in Year 1 (no adjustment to
 * show), phases set to Fixed (no inflation applied), and a genuinely £0
 * total (nothing to project).
 */
function InflatedAtStartValue({ phase, amount, inflationAnnualPct }: Readonly<{ phase: SpendingPhase; amount: number; inflationAnnualPct: number }>) {
  if (phase.inflationLinked === false || phase.startYear <= 1 || amount === 0) return null;
  const projected = projectAmountToPhaseStart(amount, inflationAnnualPct, phase.startYear);
  return <span className="inflated-value">(&asymp;{formatCurrency(projected)} at Yr {phase.startYear})</span>;
}

interface SpendingPhasesTableProps {
  phases: ReadonlyArray<SpendingPhase>;
  inflationAnnualPct: number;
  onRowClick: (phaseIndex: number) => void;
  onAddPhase: () => void;
  persons?: ReadonlyArray<PersonData>;
  planAsOfDate?: string;
  personDeceasedAges?: ReadonlyArray<PersonDeceasedAge>;
  projectionYears?: number;
}

function SpendingPhasesTable({ phases, inflationAnnualPct, onRowClick, onAddPhase, persons, planAsOfDate, personDeceasedAges, projectionYears }: Readonly<SpendingPhasesTableProps>) {
  return (
    <div className="spending-phases-table-wrapper">
      <div className="spending-phases-toolbar">
        <span className="spending-phases-toolbar-meta">
          {phases.length} phase{phases.length === 1 ? '' : 's'}
          <span className="spending-phases-toolbar-hint"> · all figures £/yr in today&apos;s money · <i>italic</i> = inflated value when the phase starts · max {MAX_SPENDING_PHASES}</span>
        </span>
        <button
          type="button"
          className="add-holding-btn add-holding-btn-toolbar"
          onClick={onAddPhase}
          disabled={phases.length >= MAX_SPENDING_PHASES}
        >
          + Add Phase
        </button>
      </div>

      <table className="spending-phases-table">
        <thead>
          <tr>
            <th className="col-phase-name">Phase</th>
            {CATEGORY_ORDER.map((key) => (
              <th key={key} className="col-value">{CATEGORY_META[key].label}</th>
            ))}
            <th className="col-value">Total</th>
            <th className="col-chevron" aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {phases.map((phase, index) => {
            const ageRange = formatPhaseAgeRange(phase, persons, planAsOfDate, personDeceasedAges, projectionYears);
            const calendarYearRange = formatPhaseCalendarYearRange(phase, planAsOfDate);
            return (
            <tr
              key={phase.phaseId}
              className="spending-phase-row"
              tabIndex={0}
              role="button"
              aria-label={`Edit ${phase.label || 'phase'}`}
              onClick={() => onRowClick(index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRowClick(index);
                }
              }}
            >
              <td className="col-phase-name">
                <span className="spending-phase-label">{phase.label || 'Untitled phase'}</span>
                <span className="spending-phase-years">
                  Year {phase.startYear}{phase.endYear ? `–${phase.endYear}` : ' onwards'}
                  {calendarYearRange && <span className="spending-phase-calendar-years"> ({calendarYearRange})</span>}
                  {ageRange && <span className="spending-phase-ages"> &middot; {ageRange}</span>}
                </span>
              </td>
              {CATEGORY_ORDER.map((key) => {
                const items = getCategoryItems(phase, key);
                const categoryTotal = getCategoryTotal(phase, key);
                return (
                  <td key={key} className="col-value tabular-nums">
                    {formatCurrency(categoryTotal)}
                    {items && (
                      <span className="spending-phase-item-count">
                        {items.length} item{items.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </td>
                );
              })}
              <td className="col-value tabular-nums">
                {formatCurrency(getPhaseTotal(phase))}
                <InflatedAtStartValue phase={phase} amount={getPhaseTotal(phase)} inflationAnnualPct={inflationAnnualPct} />
              </td>
              <td className="col-chevron" aria-hidden="true"><span className="row-chevron">&#8250;</span></td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default SpendingPhasesTable;
